import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { Button, Stack, Grid, Box, Typography } from '@mui/material';
import Iconify from '../components/iconify';
import { LightTooltip } from '../components/custom-components/custom-tooltip';
import { useStorageOperations } from '../hooks/useStorageOperations';
import { DEFAULT_PAGE_SIZE, STORAGE_COLORS } from '../utils/storageConstants';
import { applySortFilter, getComparator } from '../utils/storageUtils';
import { AddBucketDialog, UsersTable, BucketsTable, StorageStatsCards } from '../components/storage';

export default function StorageManagerPage() {
  const [addBucketDialogOpen, setAddBucketDialogOpen] = useState(false);
  const [usersOrder, setUsersOrder] = useState('asc');
  const [usersOrderBy, setUsersOrderBy] = useState('name');
  const [bucketsOrder, setBucketsOrder] = useState('asc');
  const [bucketsOrderBy, setBucketsOrderBy] = useState('name');
  const [usersFilterName, setUsersFilterName] = useState('');
  const [bucketsFilterName, setBucketsFilterName] = useState('');
  const [usersPage, setUsersPage] = useState(0);
  const [bucketsPage, setBucketsPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [bucketsRowsPerPage, setBucketsRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedBuckets, setSelectedBuckets] = useState([]);

  const { users, buckets, loading, createBucket, getStorageStats } = useStorageOperations();

  const filteredUsers = applySortFilter(users, getComparator(usersOrder, usersOrderBy), usersFilterName);
  const filteredBuckets = applySortFilter(buckets, getComparator(bucketsOrder, bucketsOrderBy), bucketsFilterName);

  const usersIsNotFound = !filteredUsers.length && !!usersFilterName;
  const bucketsIsNotFound = !filteredBuckets.length && !!bucketsFilterName;

  const storageStats = getStorageStats();

  const handleUsersRequestSort = (property) => {
    const isAsc = usersOrderBy === property && usersOrder === 'asc';
    setUsersOrder(isAsc ? 'desc' : 'asc');
    setUsersOrderBy(property);
  };

  const handleBucketsRequestSort = (property) => {
    const isAsc = bucketsOrderBy === property && bucketsOrder === 'asc';
    setBucketsOrder(isAsc ? 'desc' : 'asc');
    setBucketsOrderBy(property);
  };

  const handleUsersSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = users.map((n) => n.name);
      setSelectedUsers(newSelecteds);
      return;
    }
    setSelectedUsers([]);
  };

  const handleBucketsSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = buckets.map((n) => n.name);
      setSelectedBuckets(newSelecteds);
      return;
    }
    setSelectedBuckets([]);
  };

  const handleUsersClick = (event, name) => {
    const selectedIndex = selectedUsers.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedUsers, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedUsers.slice(1));
    } else if (selectedIndex === selectedUsers.length - 1) {
      newSelected = newSelected.concat(selectedUsers.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selectedUsers.slice(0, selectedIndex), selectedUsers.slice(selectedIndex + 1));
    }
    setSelectedUsers(newSelected);
  };

  const handleBucketsClick = (event, name) => {
    const selectedIndex = selectedBuckets.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedBuckets, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedBuckets.slice(1));
    } else if (selectedIndex === selectedBuckets.length - 1) {
      newSelected = newSelected.concat(selectedBuckets.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedBuckets.slice(0, selectedIndex),
        selectedBuckets.slice(selectedIndex + 1)
      );
    }
    setSelectedBuckets(newSelected);
  };

  const handleUsersChangePage = (event, newPage) => {
    setUsersPage(newPage);
  };

  const handleBucketsChangePage = (event, newPage) => {
    setBucketsPage(newPage);
  };

  const handleUsersChangeRowsPerPage = (event) => {
    setUsersPage(0);
    setUsersRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleBucketsChangeRowsPerPage = (event) => {
    setBucketsPage(0);
    setBucketsRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleUsersFilterByName = (event) => {
    setUsersPage(0);
    setUsersFilterName(event.target.value);
  };

  const handleBucketsFilterByName = (event) => {
    setBucketsPage(0);
    setBucketsFilterName(event.target.value);
  };

  const handleAddBucketDialogOpen = () => {
    setAddBucketDialogOpen(true);
  };

  const handleAddBucketDialogClose = () => {
    setAddBucketDialogOpen(false);
  };

  const usersEmptyRows = usersPage > 0 ? Math.max(0, (1 + usersPage) * usersRowsPerPage - filteredUsers?.length) : 0;
  const bucketsEmptyRows =
    bucketsPage > 0 ? Math.max(0, (1 + bucketsPage) * bucketsRowsPerPage - filteredBuckets?.length) : 0;

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Storage Manager</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Box>
              <Typography variant="h4" gutterBottom>
                Storage Manager
              </Typography>
              <Typography variant="body1">View all storage details and manage storage information.</Typography>
            </Box>
            <LightTooltip title="Add a new bucket">
              <Button
                variant="contained"
                onClick={handleAddBucketDialogOpen}
                startIcon={<Iconify icon="eva:plus-fill" />}
                style={{
                  backgroundColor: STORAGE_COLORS.PRIMARY,
                }}
              >
                Add Bucket
              </Button>
            </LightTooltip>
          </Stack>

          <StorageStatsCards storageStats={storageStats} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <UsersTable
                users={users}
                filteredUsers={filteredUsers}
                usersOrder={usersOrder}
                usersOrderBy={usersOrderBy}
                usersFilterName={usersFilterName}
                usersPage={usersPage}
                usersRowsPerPage={usersRowsPerPage}
                selectedUsers={selectedUsers}
                usersIsNotFound={usersIsNotFound}
                usersEmptyRows={usersEmptyRows}
                onRequestSort={handleUsersRequestSort}
                onFilterByName={handleUsersFilterByName}
                onChangePage={handleUsersChangePage}
                onChangeRowsPerPage={handleUsersChangeRowsPerPage}
                onSelectAllClick={handleUsersSelectAllClick}
                onSelectClick={handleUsersClick}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <BucketsTable
                buckets={buckets}
                filteredBuckets={filteredBuckets}
                bucketsOrder={bucketsOrder}
                bucketsOrderBy={bucketsOrderBy}
                bucketsFilterName={bucketsFilterName}
                bucketsPage={bucketsPage}
                bucketsRowsPerPage={bucketsRowsPerPage}
                selectedBuckets={selectedBuckets}
                bucketsIsNotFound={bucketsIsNotFound}
                bucketsEmptyRows={bucketsEmptyRows}
                onRequestSort={handleBucketsRequestSort}
                onFilterByName={handleBucketsFilterByName}
                onChangePage={handleBucketsChangePage}
                onChangeRowsPerPage={handleBucketsChangeRowsPerPage}
                onSelectAllClick={handleBucketsSelectAllClick}
                onSelectClick={handleBucketsClick}
              />
            </Grid>
          </Grid>

          <AddBucketDialog
            open={addBucketDialogOpen}
            onClose={handleAddBucketDialogClose}
            onSubmit={createBucket}
            loading={loading}
            users={users}
          />
        </Grid>
      </Grid>
    </>
  );
}
